import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import MenuFilters from './components/MenuFilters';
import MenuCard from './components/MenuCard';
import BuildYourPlate from './components/BuildYourPlate';
import CartSummary from './components/CartSummary';

const InteractiveMenu = () => {
  const [activeFilters, setActiveFilters] = useState({
    category: 'all',
    dietary: 'all',
    spiceLevel: 'all',
    mealType: 'all',
    preparationTime: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showBuildPlate, setShowBuildPlate] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  const menuItems = [
    {
      id: 1,
      name: "Traditional Lamb Kofta",
      category: "kebab-classics",
      price: 18.99,
      image: "https://images.pexels.com/photos/4518843/pexels-photo-4518843.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Hand-rolled lamb meatballs seasoned with traditional Mediterranean spices, grilled to perfection and served with tahini sauce.",
      ingredients: ["Ground lamb", "Onions", "Parsley", "Cumin", "Coriander", "Tahini", "Garlic"],
      dietary: ["halal"],
      spiceLevel: "medium",
      mealType: "main",
      preparationTime: "15-20",
      allergens: ["sesame"],
      nutritional: { calories: 420, protein: 28, carbs: 8, fat: 32 },
      chefRecommended: true,
      seasonal: false,
      culturalContext: `Kofta has been a cornerstone of Middle Eastern cuisine for over 1,000 years. Our family recipe combines traditional spicing techniques passed down through generations, creating the perfect balance of herbs and spices that make each bite a celebration of authentic flavors.`,
      preparationVideo: "https://example.com/kofta-prep",
      perfectPairings: ["Hummus & Pita", "Mediterranean Rice", "Grilled Vegetables"]
    },
    {
      id: 2,
      name: "Chicken Shawarma Wrap",
      category: "kebab-classics",
      price: 14.99,
      image: "https://images.pexels.com/photos/4518844/pexels-photo-4518844.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Tender marinated chicken carved fresh from the rotisserie, wrapped in warm pita with fresh vegetables and garlic sauce.",
      ingredients: ["Chicken thigh", "Pita bread", "Lettuce", "Tomatoes", "Onions", "Garlic sauce", "Pickles"],
      dietary: ["halal"],
      spiceLevel: "mild",
      mealType: "main",
      preparationTime: "10-15",
      allergens: ["gluten", "dairy"],
      nutritional: { calories: 380, protein: 32, carbs: 28, fat: 18 },
      chefRecommended: false,
      seasonal: false,
      culturalContext: `Shawarma originated in the Ottoman Empire and has become a beloved street food across the Mediterranean. Our marinade process takes 24 hours, ensuring every piece of chicken is infused with aromatic spices.`,
      preparationVideo: "https://example.com/shawarma-prep",
      perfectPairings: ["Fattoush Salad", "Hummus", "Baklava"]
    },
    {
      id: 3,
      name: "Vegetarian Falafel Platter",
      category: "mediterranean-mezze",
      price: 16.99,
      image: "https://images.pexels.com/photos/6275193/pexels-photo-6275193.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Crispy chickpea fritters served with hummus, baba ganoush, tabbouleh, and warm pita bread.",
      ingredients: ["Chickpeas", "Parsley", "Cilantro", "Onions", "Garlic", "Cumin", "Tahini"],
      dietary: ["vegetarian", "vegan"],
      spiceLevel: "mild",
      mealType: "main",
      preparationTime: "12-18",
      allergens: ["sesame"],
      nutritional: { calories: 340, protein: 18, carbs: 42, fat: 14 },
      chefRecommended: true,
      seasonal: false,
      culturalContext: `Falafel is believed to have originated in Egypt over 1,000 years ago. Our recipe uses a blend of chickpeas and fresh herbs, creating a crispy exterior and fluffy interior that represents the perfect falafel texture.`,
      preparationVideo: "https://example.com/falafel-prep",
      perfectPairings: ["Greek Salad", "Stuffed Grape Leaves", "Mint Tea"]
    },
    {
      id: 4,
      name: "Mediterranean Mezze Platter",
      category: "mediterranean-mezze",
      price: 22.99,
      image: "https://images.pexels.com/photos/5638732/pexels-photo-5638732.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "A generous selection of hummus, baba ganoush, tabbouleh, olives, feta cheese, and warm pita bread.",
      ingredients: ["Hummus", "Baba ganoush", "Tabbouleh", "Kalamata olives", "Feta cheese", "Pita bread"],
      dietary: ["vegetarian"],
      spiceLevel: "mild",
      mealType: "appetizer",
      preparationTime: "8-12",
      allergens: ["dairy", "gluten"],
      nutritional: { calories: 480, protein: 16, carbs: 38, fat: 32 },
      chefRecommended: false,
      seasonal: false,
      culturalContext: `Mezze represents the Mediterranean philosophy of sharing and community. This platter brings together the essential flavors that have been enjoyed across the region for centuries, perfect for sharing with friends and family.`,
      preparationVideo: "https://example.com/mezze-prep",
      perfectPairings: ["Red Wine", "Grilled Halloumi", "Fresh Fruit"]
    },
    {
      id: 5,
      name: "Fattoush Salad",
      category: "fresh-salads",
      price: 12.99,
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Fresh mixed greens with tomatoes, cucumbers, radishes, and crispy pita chips, dressed with sumac vinaigrette.",
      ingredients: ["Mixed greens", "Tomatoes", "Cucumbers", "Radishes", "Pita chips", "Sumac", "Olive oil"],
      dietary: ["vegetarian", "vegan"],
      spiceLevel: "mild",
      mealType: "appetizer",
      preparationTime: "5-8",
      allergens: ["gluten"],
      nutritional: { calories: 220, protein: 6, carbs: 28, fat: 12 },
      chefRecommended: false,
      seasonal: true,
      culturalContext: `Fattoush is a traditional Levantine salad that transforms day-old bread into a delicious component. The sumac adds a tangy, lemony flavor that's characteristic of Middle Eastern cuisine.`,
      preparationVideo: "https://example.com/fattoush-prep",
      perfectPairings: ["Grilled Chicken", "Lamb Kebab", "Lentil Soup"]
    },
    {
      id: 6,
      name: "Grilled Halloumi Salad",
      category: "fresh-salads",
      price: 15.99,
      image: "https://images.pexels.com/photos/1640772/pexels-photo-1640772.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Warm grilled halloumi cheese over mixed greens with cherry tomatoes, cucumber, and pomegranate vinaigrette.",
      ingredients: ["Halloumi cheese", "Mixed greens", "Cherry tomatoes", "Cucumber", "Pomegranate molasses", "Mint"],
      dietary: ["vegetarian", "gluten-free"],
      spiceLevel: "mild",
      mealType: "main",
      preparationTime: "10-15",
      allergens: ["dairy"],
      nutritional: { calories: 320, protein: 18, carbs: 16, fat: 24 },
      chefRecommended: true,
      seasonal: true,
      culturalContext: `Halloumi, Cyprus's national cheese, has been made for centuries using traditional methods. When grilled, it develops a beautiful golden crust while maintaining its unique squeaky texture inside.`,
      preparationVideo: "https://example.com/halloumi-prep",
      perfectPairings: ["Pita Bread", "Olive Tapenade", "White Wine"]
    },
    {
      id: 7,
      name: "Mediterranean Rice Pilaf",
      category: "traditional-sides",
      price: 8.99,
      image: "https://images.pexels.com/photos/4518845/pexels-photo-4518845.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Aromatic basmati rice cooked with saffron, almonds, and dried fruits, finished with fresh herbs.",
      ingredients: ["Basmati rice", "Saffron", "Almonds", "Raisins", "Pine nuts", "Fresh herbs"],
      dietary: ["vegetarian", "vegan", "gluten-free"],
      spiceLevel: "mild",
      mealType: "side",
      preparationTime: "20-25",
      allergens: ["nuts"],
      nutritional: { calories: 280, protein: 6, carbs: 52, fat: 8 },
      chefRecommended: false,
      seasonal: false,
      culturalContext: `Rice pilaf has been a staple across the Mediterranean and Middle East for over 1,000 years. Our version combines Persian saffron with Turkish cooking techniques for a truly aromatic experience.`,
      preparationVideo: "https://example.com/pilaf-prep",
      perfectPairings: ["Lamb Kebab", "Grilled Vegetables", "Yogurt Sauce"]
    },
    {
      id: 8,
      name: "Honey Baklava",
      category: "sweet-endings",
      price: 6.99,
      image: "https://images.pexels.com/photos/4518846/pexels-photo-4518846.jpeg?auto=compress&cs=tinysrgb&w=800",
      description: "Layers of crispy phyllo pastry filled with chopped nuts and sweetened with honey syrup.",
      ingredients: ["Phyllo pastry", "Walnuts", "Pistachios", "Honey", "Butter", "Cinnamon"],
      dietary: ["vegetarian"],
      spiceLevel: "none",
      mealType: "dessert",
      preparationTime: "5-8",
      allergens: ["nuts", "gluten", "dairy"],
      nutritional: { calories: 320, protein: 6, carbs: 28, fat: 22 },
      chefRecommended: true,
      seasonal: false,
      culturalContext: `Baklava is the crown jewel of Mediterranean desserts, with origins dating back to the Ottoman Empire. Each piece requires dozens of paper-thin phyllo layers, representing the patience and skill of traditional pastry making.`,
      preparationVideo: "https://example.com/baklava-prep",
      perfectPairings: ["Turkish Coffee", "Mint Tea", "Fresh Berries"]
    }
  ];

  const categories = [
    { id: 'all', name: 'All Items', icon: 'Grid3X3' },
    { id: 'kebab-classics', name: 'Kebab Classics', icon: 'ChefHat' },
    { id: 'mediterranean-mezze', name: 'Mediterranean Mezze', icon: 'Utensils' },
    { id: 'fresh-salads', name: 'Fresh Salads', icon: 'Salad' },
    { id: 'traditional-sides', name: 'Traditional Sides', icon: 'Bowl' },
    { id: 'sweet-endings', name: 'Sweet Endings', icon: 'Cake' }
  ];

  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.ingredients.some(ing => ing.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = activeFilters.category === 'all' || item.category === activeFilters.category;
      const matchesDietary = activeFilters.dietary === 'all' || item.dietary.includes(activeFilters.dietary);
      const matchesSpice = activeFilters.spiceLevel === 'all' || item.spiceLevel === activeFilters.spiceLevel;
      const matchesMealType = activeFilters.mealType === 'all' || item.mealType === activeFilters.mealType;
      const matchesTime = activeFilters.preparationTime === 'all' || 
                         (activeFilters.preparationTime === 'quick' && parseInt(item.preparationTime.split('-')[0]) <= 15) ||
                         (activeFilters.preparationTime === 'medium' && parseInt(item.preparationTime.split('-')[0]) > 15 && parseInt(item.preparationTime.split('-')[0]) <= 25) ||
                         (activeFilters.preparationTime === 'slow' && parseInt(item.preparationTime.split('-')[0]) > 25);

      return matchesSearch && matchesCategory && matchesDietary && matchesSpice && matchesMealType && matchesTime;
    });
  }, [menuItems, activeFilters, searchQuery]);

  const addToCart = (item, quantity = 1, customizations = {}) => {
    const cartItem = {
      ...item,
      quantity,
      customizations,
      cartId: Date.now() + Math.random()
    };
    setCart(prev => [...prev, cartItem]);
  };

  const removeFromCart = (cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
  };

  const updateCartQuantity = (cartId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartId);
      return;
    }
    setCart(prev => prev.map(item => 
      item.cartId === cartId ? { ...item, quantity } : item
    ));
  };

  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-50 to-accent-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Discover Authentic Flavors
            </h1>
            <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto mb-8">
              Explore our carefully crafted menu where every dish tells a story of Mediterranean tradition and culinary excellence. From time-honored kebab classics to fresh seasonal specialties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowBuildPlate(true)}
                className="btn-primary"
              >
                <Icon name="Plus" size={20} className="mr-2" />
                Build Your Plate
              </button>
              <Link to="/order-online-streamlined-experience" className="btn-conversion">
                <Icon name="ShoppingBag" size={20} className="mr-2" />
                Order Online
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder="Search dishes or ingredients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-organic"
              />
            </div>

            {/* Quick Stats */}
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-1 text-accent-600">
                <Icon name="Star" size={16} className="fill-current" />
                <span className="font-semibold">Chef's Picks Available</span>
              </div>
              <div className="flex items-center space-x-1 text-trust-600">
                <Icon name="Leaf" size={16} />
                <span className="font-medium">Fresh Daily</span>
              </div>
              <div className="flex items-center space-x-1 text-success-600">
                <Icon name="Clock" size={16} />
                <span className="font-medium">Quick Prep Options</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-80">
            <MenuFilters
              categories={categories}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
            />
          </div>

          {/* Menu Items Grid */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-headline text-2xl font-semibold text-text-primary">
                  {activeFilters.category === 'all' ? 'All Menu Items' : 
                   categories.find(cat => cat.id === activeFilters.category)?.name}
                </h2>
                <p className="text-text-secondary">
                  {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} found
                </p>
              </div>
              
              {/* View Toggle */}
              <div className="flex items-center space-x-2">
                <button className="p-2 rounded-lg bg-primary-100 text-primary">
                  <Icon name="Grid3X3" size={20} />
                </button>
                <button className="p-2 rounded-lg text-text-secondary hover:bg-primary-50 hover:text-primary transition-organic">
                  <Icon name="List" size={20} />
                </button>
              </div>
            </div>

            {/* Menu Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredItems.map(item => (
                <MenuCard
                  key={item.id}
                  item={item}
                  onAddToCart={addToCart}
                />
              ))}
            </div>

            {/* No Results */}
            {filteredItems.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-primary-100 rounded-full mb-6">
                  <Icon name="Search" size={48} className="text-primary" />
                </div>
                <h3 className="font-headline text-2xl font-semibold text-text-primary mb-4">
                  No dishes found
                </h3>
                <p className="text-text-secondary mb-6 max-w-md mx-auto">
                  Try adjusting your filters or search terms to discover more delicious options from our menu.
                </p>
                <button
                  onClick={() => {
                    setActiveFilters({
                      category: 'all',
                      dietary: 'all',
                      spiceLevel: 'all',
                      mealType: 'all',
                      preparationTime: 'all'
                    });
                    setSearchQuery('');
                  }}
                  className="btn-primary"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Build Your Plate Modal */}
      {showBuildPlate && (
        <BuildYourPlate
          menuItems={menuItems}
          onClose={() => setShowBuildPlate(false)}
          onAddToCart={addToCart}
        />
      )}

      {/* Cart Summary */}
      {cartItemCount > 0 && (
        <CartSummary
          cart={cart}
          cartTotal={cartTotal}
          cartItemCount={cartItemCount}
          onUpdateQuantity={updateCartQuantity}
          onRemoveItem={removeFromCart}
          showCart={showCart}
          setShowCart={setShowCart}
        />
      )}

      {/* Floating Cart Button */}
      {cartItemCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-conversion text-white rounded-full p-4 shadow-warm-lg hover:bg-conversion-600 transition-organic z-40"
        >
          <div className="relative">
            <Icon name="ShoppingBag" size={24} />
            <span className="absolute -top-2 -right-2 bg-accent text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-semibold">
              {cartItemCount}
            </span>
          </div>
        </button>
      )}

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-primary to-secondary py-16 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-headline text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Authentic Mediterranean Flavors?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have made Kebab Palace their go-to destination for authentic Mediterranean cuisine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/order-online-streamlined-experience" className="bg-white text-primary font-cta font-semibold px-8 py-4 rounded-lg shadow-warm hover:bg-primary-50 transition-organic">
              Order Now
            </Link>
            <Link to="/our-story-heritage-authenticity" className="border-2 border-white text-white font-cta font-semibold px-8 py-4 rounded-lg hover:bg-white hover:text-primary transition-organic">
              Our Story
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InteractiveMenu;